import { useState } from "react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import api, { getErrorMessage } from "@/lib/api";
import { useConfirm } from "@/hooks/useConfirm";
import { formatDistanceToNow } from "date-fns";

// Human-readable strings for system-auto-pause reasons. The raw value
// (e.g. "2fa_no_delivery") is written by the orchestrator in
// services/auto_pause_orchestrator.py; we map it to a sentence here so
// the tooltip reads naturally to a tenant instead of as a system code.
const SYSTEM_PAUSE_REASON_TEXT = {
  "2fa_no_delivery": "2FA code didn't arrive (24h)",
};

/**
 * PortalPauseToggle — inline Switch that pauses or resumes a portal's
 * cron-scraping via PATCH /api/catalog/portals/{portal_id}/pause.
 *
 * Props:
 *   portal       — the portal row from /my-portals (must have portal_id)
 *   onUpdate     — (updatedFields: object) → void   called on success with
 *                  { auth_paused_at, auth_paused_by, auth_paused_reason }
 *                  from the SubscriptionDoc returned by the PATCH endpoint
 */
export default function PortalPauseToggle({ portal, onUpdate }) {
  const isPaused = Boolean(portal.auth_paused_at);
  const [pending, setPending] = useState(false);
  const confirm = useConfirm();

  /** Format an ISO date string as relative time, or null. */
  function relativeTime(dateStr) {
    if (!dateStr) return null;
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return null;
      return formatDistanceToNow(d, { addSuffix: true });
    } catch {
      return null;
    }
  }

  /**
   * Tooltip middle line. Branches on whether the pause was issued by a
   * person (manual: "Paused 3 hours ago by alice@…") or by the system
   * (auto-pause orchestrator: "Paused automatically 3 hours ago —
   * 2FA code didn't arrive (24h)"). The mechanical "by system" / quoted
   * raw-reason rendering is suppressed for system pauses since
   * `auth_paused_by="system"` + `auth_paused_reason="2fa_no_delivery"`
   * reads as gibberish to a tenant — see PORTAL_PAUSE_REASON_TEXT.
   */
  function pausedLine() {
    const rel = relativeTime(portal.auth_paused_at);

    if (portal.auth_paused_by === "system") {
      const reasonText = portal.auth_paused_reason
        ? SYSTEM_PAUSE_REASON_TEXT[portal.auth_paused_reason] ||
          portal.auth_paused_reason
        : null;
      if (rel && reasonText) return `automatically ${rel} — ${reasonText}`;
      if (rel) return `automatically ${rel}`;
      if (reasonText) return `automatically — ${reasonText}`;
      return "automatically";
    }

    const parts = [];
    if (rel) parts.push(rel);
    if (portal.auth_paused_by) parts.push(`by ${portal.auth_paused_by}`);
    if (portal.auth_paused_reason) parts.push(`"${portal.auth_paused_reason}"`);
    return parts.length ? parts.join(" ") : null;
  }

  // Last-success line shown below the switch when the portal is paused.
  const lastSuccessRel = relativeTime(portal.health?.last_success_at);

  async function handleToggle() {
    const nextPaused = !isPaused;

    // Confirm before pausing (resuming is harmless, so it stays instant).
    if (nextPaused) {
      const name = portal.display_name || portal.portal_name || "this portal";
      const ok = await confirm({
        title: `Pause ${name}?`,
        description:
          "The scraper will skip this portal on the next cron run until you resume it. No historical documents are lost.",
        confirmText: "Pause",
      });
      if (!ok) return;
    }

    setPending(true);

    // Optimistic update — immediately reflect the new state in the parent
    const optimisticNow = nextPaused ? new Date().toISOString() : null;
    onUpdate({
      auth_paused_at: optimisticNow,
      auth_paused_by: portal.auth_paused_by ?? null,
      auth_paused_reason: portal.auth_paused_reason ?? null,
    });

    try {
      const res = await api.patch(
        `/catalog/portals/${portal.portal_id}/pause`,
        { paused: nextPaused }
      );
      // Confirm with actual values returned by the SubscriptionDoc
      const doc = res.data;
      onUpdate({
        auth_paused_at: doc.auth_paused_at ?? null,
        auth_paused_by: doc.auth_paused_by ?? null,
        auth_paused_reason: doc.auth_paused_reason ?? null,
      });
      if (nextPaused) {
        toast.success(
          "Portal paused. Won't lose historical docs. You can resume any time."
        );
      } else {
        toast.success("Portal resumed. It will run on the next cron cycle.");
      }
    } catch (err) {
      // Revert to the original values
      onUpdate({
        auth_paused_at: portal.auth_paused_at ?? null,
        auth_paused_by: portal.auth_paused_by ?? null,
        auth_paused_reason: portal.auth_paused_reason ?? null,
      });
      toast.error(
        getErrorMessage(
          err,
          nextPaused ? "Failed to pause portal" : "Failed to resume portal"
        )
      );
    } finally {
      setPending(false);
    }
  }

  const tooltipContent = isPaused ? (
    <div className="space-y-1 max-w-xs">
      <p className="font-semibold">Paused &mdash; cron skips this portal</p>
      {pausedLine() && (
        <p className="text-xs text-gray-300">Paused {pausedLine()}</p>
      )}
      {lastSuccessRel && (
        <p className="text-xs text-gray-300">Last success: {lastSuccessRel}</p>
      )}
      <p className="text-xs text-gray-400 mt-1">
        Won&rsquo;t lose historical docs. Toggle off to resume.
      </p>
    </div>
  ) : (
    <div className="max-w-xs">
      <p className="font-semibold">Pause cron for this portal</p>
      <p className="text-xs text-gray-300 mt-0.5">
        Skips the next cron run. Won&rsquo;t lose historical docs. You can resume any time.
      </p>
    </div>
  );

  return (
    <div className="inline-flex flex-col items-start gap-0.5">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center gap-2 cursor-default">
              <Switch
                checked={isPaused}
                onCheckedChange={handleToggle}
                disabled={pending}
                aria-label={
                  isPaused
                    ? `Resume ${portal.display_name || portal.portal_name || "portal"} on next cron`
                    : `Pause ${portal.display_name || portal.portal_name || "portal"} from cron`
                }
                className={
                  isPaused
                    ? "data-[state=checked]:bg-amber-500"
                    : undefined
                }
              />
              {isPaused && (
                <span
                  className="text-xs font-medium text-amber-700"
                  data-testid="paused-label"
                >
                  Paused
                </span>
              )}
            </span>
          </TooltipTrigger>
          <TooltipContent side="left">{tooltipContent}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      {isPaused && lastSuccessRel && (
        <span className="text-xs text-gray-400 pl-0.5">
          Last success {lastSuccessRel}
        </span>
      )}
    </div>
  );
}
